import type { EnrichedAccount } from '@openx/types/account';
import type { InstanceType } from '@openx/types/instance';
import type { AclFlag, AclMapping } from '@openx/types/session';
import type { RealUser, User } from '@openx/types/user';
import { useCallback, useMemo } from 'react';
import { useSessionQuery } from '../session/useSessionQuery';

export interface PermissionsCheckParams {
  accessUser?: User;
  accessAccount?: EnrichedAccount;
  aclMapping?: AclMapping;
  userAcl?: AclFlag[];
  accountAcl?: AclFlag[];
  aclList?: AclFlag[];
  instance?: InstanceType;
  realUser?: RealUser;
}

export type PermissionsCheck = (params: PermissionsCheckParams) => boolean;
export type IsAllowedCheck = (check: PermissionsCheck) => boolean;
export type UseIsAllowedReturnType = PermissionsCheckParams & {
  isAllowed: IsAllowedCheck;
  aclList: AclFlag[];
  aclMapping: AclMapping;
};

export function usePermissionsCheckParams(): PermissionsCheckParams {
  const { data } = useSessionQuery();

  return useMemo(
    () => ({
      accessAccount: data.details.account,
      accessUser: data.details.user,
      accountAcl: data.acl?.account,
      aclList: data.acl?.list,
      aclMapping: data.acl?.mapping,
      instance: data.details.instance,
      realUser: data.details.real_user,
      userAcl: data.acl?.user,
    }),
    [data]
  );
}

export function useIsAllowed(): UseIsAllowedReturnType {
  const params = usePermissionsCheckParams();

  const isAllowed = useCallback((check: PermissionsCheck): boolean => check(params), [params]);

  return {
    ...params,
    aclList: params.aclList ?? [],
    aclMapping: params.aclMapping ?? {},
    isAllowed,
  };
}
